import { FunctionComponent, useState } from 'react'
import { StaffSlots } from '@/types/StaffRate'
import { Work } from '@/types/work'
import { Daycare } from '@/types/Daycare'
import { StaffWithLinks } from '@/types/Staff'
import styles from './draftingSlots.module.scss'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon'
import { Add, Person } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import moment from 'moment/moment'
import { EventCalendar } from '@/types/Calendar'
import { defineStaffSlots, TimBarSlots } from '../../TimBarSlots/defineSlots'
import TimeBarSlots from '../../TimBarSlots/timeBarSlots'
import { defineWorkTypeToCalendarEventType } from '../../../planning/utils/eventsWorks'
import EditOrCreateWorkDialog from '@/container/planning/Dialog/staffWork/editOrCreateStaffWork/editOrCreateWorkDialog'
import { formatHoursMinutes, getHoursAndMinutesFromWorks } from '@/container/planning/calendar/utils/calendarHelpers'

interface DraftingStaffSlotsProps {
    staffSlots: StaffSlots
    opening: string
    closing: string
    date: string
    daycare: Daycare
    staffsListData: StaffWithLinks[]
    allWorks: Work[]
    canWriteStaffWorks: boolean
    refetchData: () => void
    setSelectedEvent: (event: EventCalendar | null) => void
    setShowSlotDialogOpen: (open: boolean) => void
}

const DraftingStaffSlots: FunctionComponent<DraftingStaffSlotsProps> = ({
    staffSlots,
    opening,
    closing,
    date,
    daycare,
    staffsListData,
    allWorks,
    canWriteStaffWorks,
    refetchData,
    setSelectedEvent,
    setShowSlotDialogOpen
}: DraftingStaffSlotsProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const { hours: staffHours, minutes: staffMinutes } = getHoursAndMinutesFromWorks(staffSlots.works)

    const totalMinutes: number = moment.duration(moment(closing, 'HH:mm').diff(moment(opening, 'HH:mm'))).asMinutes()

    const timBarSlots: TimBarSlots[] = defineStaffSlots(staffSlots.works, opening, closing)

    const [isAddDialogOpen, setAddDialogOpen] = useState<boolean>(false)
    const [addDialogFrom, setAddDialogFrom] = useState<string>(opening)
    const [addDialogTo, setAddDialogTo] = useState<string>(closing)

    function openAddDialog(from: string, to: string): void {
        setAddDialogFrom(from)
        setAddDialogTo(to)
        setAddDialogOpen(true)
    }

    function handleSelectWork(slot: TimBarSlots): void {
        if (slot.id) {
            const staffSlot: Work = staffSlots.works.find((work: Work) => work.id === slot.id)!
            setSelectedEvent({
                title: staffSlot.staffName,
                start: moment(staffSlot.start, 'HH:mm').toDate(),
                end: moment(staffSlot.end, 'HH:mm').toDate(),
                number: '',
                type: defineWorkTypeToCalendarEventType(staffSlot.type),
                staffId: staffSlot.staff,
                slots: staffSlot
            })
            setShowSlotDialogOpen(true)
        } else {
            setShowSlotDialogOpen(false)
            setSelectedEvent(null)
            if (canWriteStaffWorks) {
                openAddDialog(slot.start, slot.end)
            }
        }
    }

    return (
        <>
            <div className={`${staffSlots.self && styles.LineBackgroundForMe} ${styles.Line}`}>
                <div className={styles.StaffProfile}>
                    <ButtonIcon
                        icon={<Person />}
                        className={staffSlots.self ? styles.MeButtonIcon : styles.ButtonIcon}
                        onClick={() =>
                            staffSlots.self
                                ? navigate('/account/profile')
                                : user?.currentDaycareRights.hasStaffAccess &&
                                  navigate(`/staff?profile=${staffSlots.staffId}`)
                        }
                    />
                    <div className={styles.Info}>
                        <span className={styles.Name}>{staffSlots.self ? t('me') : `${staffSlots.staffName}`}</span>
                        <span className={styles.Hours}>{formatHoursMinutes(staffHours, staffMinutes)}</span>
                    </div>
                </div>
                <div className={styles.Slots}>
                    {staffSlots.works.length === 0 ? (
                        canWriteStaffWorks && (
                            <ButtonIcon
                                icon={<Add />}
                                className={styles.AddButtonIcon}
                                onClick={() => openAddDialog(opening, closing)}
                            />
                        )
                    ) : (
                        <TimeBarSlots
                            timBarSlots={timBarSlots}
                            totalMinutes={totalMinutes}
                            handleSelect={handleSelectWork}
                            interactiveEmptySlots={canWriteStaffWorks}
                        />
                    )}
                </div>
            </div>
            {isAddDialogOpen && (
                <EditOrCreateWorkDialog
                    open={isAddDialogOpen}
                    handleClose={() => setAddDialogOpen(false)}
                    daycare={daycare}
                    staffsListData={staffsListData}
                    date={date}
                    from={addDialogFrom}
                    to={addDialogTo}
                    works={allWorks}
                    refetch={refetchData}
                    selectedStaffId={staffSlots.staffId.toString()}
                />
            )}
        </>
    )
}

export default DraftingStaffSlots
